// src/pages/CircuitPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MapPin,
  X,
  Route,
  Calendar,
  User,
  Mail,
  Phone,
  MessageSquare,
  Wallet,
  Eye,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";
import cmiService from "../services/cmiService";
import { getPaymentMode } from "../services/settingsService";
import zelijPattern from "../assets/zelij3.png";
import { destinationPlaceholders } from "../utils/imagePlaceholders";


import { t as tr } from "../contexts/translations";
// Cloudinary Marrakech image
const CLOUDINARY_MARRAKECH_IMAGE =
  "https://res.cloudinary.com/dzszitsgb/image/upload/v1773760319/nouzha/ubllqf3hfvqox1qixeby.jpg";

const CLOUDINARY_FES_IMAGE =
  "https://res.cloudinary.com/dzszitsgb/image/upload/v1773759766/nouzha/fes-20260317-new.jpg";
const CLOUDINARY_SAHARA_IMAGE =
  "https://res.cloudinary.com/dzszitsgb/image/upload/v1773759769/nouzha/sahara-20260317-new.jpg";

const PROD_API_BASE = "https://nouzhaback-theta.vercel.app";
const ENV_API_BASE = (process.env.REACT_APP_API_BASE_URL || "http://localhost:5000").replace(/\/$/, "");
const API_BASE_CANDIDATES = [ENV_API_BASE, PROD_API_BASE].filter(Boolean);

const CIRCUITS_PATH = "/api/circuits";

// ---------- utils dates ----------
const getTodayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split("T")[0];
};

// ---------- small utils ----------
const formatPrice = (v) => {
  const n = Number(v || 0);
  try {
    return new Intl.NumberFormat("fr-MA", { style: "currency", currency: "MAD" }).format(n);
  } catch {
    return `${n} MAD`;
  }
};

const safeArr = (x) => (Array.isArray(x) ? x : []);
const stopsFromCircuit = (c) => {
  const villes = safeArr(c?.villes).filter(Boolean);
  if (villes.length) return villes;
  const etapes = safeArr(c?.etapes).map((e) => e?.ville).filter(Boolean);
  return etapes;
};

const getCircuitImage = (circuit) => {
  const title = String(circuit?.titre || "").toLowerCase();

  if (title.includes("agadir")) return destinationPlaceholders.agadir;
  if (title.includes("el jadida") || title.includes("atlantique")) return destinationPlaceholders.elJadida;
  if (title.includes("chefchaouen") || title.includes("bleue") || title.includes("nord impérial")) return destinationPlaceholders.chefchaouen;
  if (title.includes("ifrane")) return destinationPlaceholders.ifrane;
  if (title.includes("tinghir")) return destinationPlaceholders.tinghir;
  if (title.includes("tanger") && title.includes("rabat")) return destinationPlaceholders.tangerRabat;
  if (title.includes("ouarzazate") && !title.includes("merzouga") && !title.includes("tinghir") && !title.includes("marrakech")) return destinationPlaceholders.ouarzazate;
  
  if (title.includes("marrakech & essaouira détente")) return destinationPlaceholders.marrakech;
  
  if (title.includes("marrakech") && title.includes("ouarzazate")) return destinationPlaceholders.marrakech;

  if (title.includes("desert classique") || title.includes("désert classique")) {
    return CLOUDINARY_SAHARA_IMAGE;
  }
  
  if (title.includes("villes royales express")) {
    return CLOUDINARY_FES_IMAGE;
  }

  return (
    circuit?.image ||
    "https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=1200&h=800&fit=crop"
  );
};

const circuitMoodImages = [CLOUDINARY_MARRAKECH_IMAGE, CLOUDINARY_SAHARA_IMAGE, CLOUDINARY_FES_IMAGE];

// ---------- Modal wrapper ----------
// ---------- Modal wrapper ----------
const ModalShell = ({ open, onClose, title, children }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!open) return;
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* ✅ scroll container */}
      <div className="relative h-full w-full overflow-y-auto px-4 py-6">
        {/* ✅ keep it visible on small screens */}
        <div className="mx-auto w-full max-w-3xl">
          <div
            ref={ref}
            className="
              relative w-full
              rounded-2xl bg-white shadow-2xl border border-orange-100
              overflow-hidden
              max-h-[calc(100vh-3rem)]
              flex flex-col
            "
          >
            {/* header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-orange-100 bg-gradient-to-r from-orange-50 to-white">
              <div className="font-bold text-gray-900">{title}</div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-orange-50 text-gray-600"
                title="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* ✅ scrollable content */}
            <div className="p-5 overflow-y-auto">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// ---------- Circuit details modal ----------
const CircuitDetailsModal = ({ open, onClose, circuit, T }) => {
  const stops = useMemo(() => stopsFromCircuit(circuit), [circuit]);

  if (!circuit) return null;

  return (
    <ModalShell open={open} onClose={onClose} title={T("circuitDetailsTitle", "Détails du circuit")}>
      <div className="space-y-5">
        {/* header */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-56 h-40 rounded-xl overflow-hidden bg-gray-100 border border-orange-100">
            <img
              src={getCircuitImage(circuit)}
              alt={circuit?.titre || "Circuit"}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src =
                  "https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=1200&h=800&fit=crop";
              }}
            />
          </div>

          <div className="flex-1">
            <h3 className="text-xl font-extrabold text-gray-900">{circuit?.titre}</h3>

            <div className="mt-2 flex flex-wrap gap-2">
              {circuit?.duree && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-100">
                  {circuit.duree}
                </span>
              )}

              {typeof circuit?.note === "number" && circuit.note > 0 && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-200">
                  ⭐ {circuit.note.toFixed(1)} {circuit?.avis ? `(${circuit.avis})` : ""}
                </span>
              )}

              {circuit?.participants && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-200">
                  👥 {circuit.participants}
                </span>
              )}

              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
                {formatPrice(circuit?.prix)}
              </span>
            </div>

            {stops.length > 0 && (
              <div className="mt-3 text-sm text-gray-700 flex items-start gap-2">
                <MapPin className="w-4 h-4 text-orange-600 mt-0.5" />
                <div className="flex-1">
                  <span className="font-semibold">{T("stops", "Villes")}:</span> {stops.join(" → ")}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* description */}
        {circuit?.description && (
          <div className="bg-orange-50/60 border border-orange-100 rounded-xl p-4">
            <div className="font-semibold text-gray-900 mb-1">{T("description", "Description")}</div>
            <p className="text-sm text-gray-700 leading-relaxed">{circuit.description}</p>
          </div>
        )}

        {/* inclus */}
        {circuit?.inclus && (
          <div className="border border-orange-100 rounded-xl p-4">
            <div className="font-semibold text-gray-900 mb-3">{T("included", "Inclus")}</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
              {circuit?.inclus?.transport && (
                <div className="p-3 rounded-xl bg-white border border-gray-100">
                  <div className="font-semibold text-gray-900 mb-1">{T("inclTransport", "Transport")}</div>
                  <div>{circuit.inclus.transport}</div>
                </div>
              )}

              {circuit?.inclus?.hebergement && (
                <div className="p-3 rounded-xl bg-white border border-gray-100">
                  <div className="font-semibold text-gray-900 mb-1">{T("inclHotel", "Hébergement")}</div>
                  <div>{circuit.inclus.hebergement}</div>
                </div>
              )}

              {circuit?.inclus?.visite && (
                <div className="p-3 rounded-xl bg-white border border-gray-100">
                  <div className="font-semibold text-gray-900 mb-1">{T("inclVisits", "Visites")}</div>
                  <div>{circuit.inclus.visite}</div>
                </div>
              )}

              {circuit?.inclus?.activites && (
                <div className="p-3 rounded-xl bg-white border border-gray-100">
                  <div className="font-semibold text-gray-900 mb-1">{T("inclActivities", "Activités")}</div>
                  <div>{circuit.inclus.activites}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* highlights */}
        {safeArr(circuit?.highlights).length > 0 && (
          <div className="border border-orange-100 rounded-xl p-4">
            <div className="font-semibold text-gray-900 mb-2">{T("highlights", "Highlights")}</div>
            <ul className="list-disc pl-6 text-sm text-gray-700 space-y-1">
              {circuit.highlights.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </div>
        )}

        {/* galerie */}
        {safeArr(circuit?.galerie).length > 0 && (
          <div className="border border-orange-100 rounded-xl p-4">
            <div className="font-semibold text-gray-900 mb-3">{T("gallery", "Galerie")}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {circuit.galerie.map((url, i) => (
                <div key={i} className="rounded-xl overflow-hidden bg-gray-100 border border-gray-100">
                  <img
                    src={url}
                    alt={`gal-${i}`}
                    className="w-full h-28 object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

// ---------- Reservation modal ----------
const ReservationModal = ({ open, onClose, circuit, language, T, onSubmit, sending, paymentMode = "direct", initialDate = "" }) => {
  const [form, setForm] = useState({
    date: initialDate || getTodayISO(),
    participants: 2,
    fullName: "",
    email: "",
    phone: "",
    address: "", // ✅ required by schema
    travelType: "couple", // ✅ required by schema enum
    acceptedCmi: false, // ✅ required by schema
    message: "",
  });

  // auto-fill user if available
  useEffect(() => {
    if (!open) return;
    const raw = localStorage.getItem("currentUser");
    if (!raw) return;

    try {
      const u = JSON.parse(raw);
      const full = u?.username || `${u?.prenom || ""} ${u?.nom || ""}`.trim();

      setForm((p) => ({
        ...p,
        fullName: p.fullName || full || "",
        email: p.email || u?.email || "",
        phone: p.phone || u?.telephone || "",
        address: p.address || u?.adresse || "",
      }));
    } catch {}
  }, [open]);

  // reset date when opening (garde la date choisie dans le hero si fournie)
  useEffect(() => {
    if (!open) return;
    setForm((p) => ({ ...p, date: initialDate || getTodayISO() }));
  }, [open, circuit?._id, initialDate]);

  const canSubmit = useMemo(() => {
    return Boolean(
      circuit?._id &&
        form.date &&
        form.fullName.trim() &&
        form.email.trim() &&
        form.phone.trim() &&
        form.address.trim() &&
        form.travelType &&
        form.acceptedCmi &&
        Number(form.participants) > 0 &&
        !sending
    );
  }, [circuit?._id, form, sending]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <ModalShell open={open} onClose={onClose} title={T("reserveTitle", "Réserver ce circuit")}>
      <div className="space-y-5">
        <div className="rounded-xl border border-orange-100 bg-orange-50/50 p-4">
          <div className="font-bold text-gray-900">{circuit?.titre}</div>
          <div className="text-sm text-gray-700 mt-1 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-orange-600" />
            <span className="font-semibold">{formatPrice(circuit?.prix)}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {T("travelDate", "Date de voyage")} *
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 w-4 h-4 text-orange-600" />
              <input
                type="date"
                value={form.date}
                min={getTodayISO()}
                onChange={(e) => setField("date", e.target.value)}
                className="w-full pl-10 pr-3 py-2 rounded-xl border border-orange-200 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:outline-none"
              />
            </div>
          </div>

          {/* participants */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {T("participants", "Participants")} *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-2.5 w-4 h-4 text-orange-600" />
              <input
                type="number"
                min={1}
                value={form.participants}
                onChange={(e) => setField("participants", e.target.value)}
                className="w-full pl-10 pr-3 py-2 rounded-xl border border-orange-200 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:outline-none"
              />
            </div>
          </div>

          {/* full name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {T("fullName", "Nom complet")} *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-2.5 w-4 h-4 text-orange-600" />
              <input
                type="text"
                value={form.fullName}
                onChange={(e) => setField("fullName", e.target.value)}
                className="w-full pl-10 pr-3 py-2 rounded-xl border border-orange-200 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:outline-none"
                placeholder={T("fullNamePlaceholder", "Ex: Nom complet")}
              />
            </div>
          </div>

          {/* email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {T("emailLabel", "Email")} *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 w-4 h-4 text-orange-600" />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                className="w-full pl-10 pr-3 py-2 rounded-xl border border-orange-200 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:outline-none"
                placeholder="vous@example.com"
              />
            </div>
          </div>

          {/* phone */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {T("phoneLabel", "Téléphone")} *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-2.5 w-4 h-4 text-orange-600" />
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                className="w-full pl-10 pr-3 py-2 rounded-xl border border-orange-200 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:outline-none"
                placeholder={T("phonePlaceholder", "Ex: +212 6xx xx xx xx")}
              />
            </div>
          </div>

          {/* address */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {T("addressLabel", "Adresse")} *
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-orange-600" />
              <input
                type="text"
                value={form.address}
                onChange={(e) => setField("address", e.target.value)}
                className="w-full pl-10 pr-3 py-2 rounded-xl border border-orange-200 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:outline-none"
                placeholder={T("addressPlaceholder", "Ex: Casablanca, Maarif...")}
              />
            </div>
          </div>

          {/* travel type */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {T("travelType", "Type de voyage")} *
            </label>
            <select
              value={form.travelType}
              onChange={(e) => setField("travelType", e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-orange-200 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:outline-none"
            >
              <option value="solo">{T("solo", "Solo")}</option>
              <option value="couple">{T("couple", "Couple")}</option>
              <option value="famille">{T("family", "Famille")}</option>
              <option value="amis">{T("friends", "Amis")}</option>
              <option value="groupe">{T("group", "Groupe")}</option>
            </select>
          </div>

          {/* accepted cmi */}
          <div className="md:col-span-2 flex items-start gap-3 rounded-xl border border-orange-100 bg-orange-50/40 p-3">
            <input
              type="checkbox"
              checked={form.acceptedCmi}
              onChange={(e) => setField("acceptedCmi", e.target.checked)}
              className="mt-1"
            />
            <div className="text-sm text-gray-700">
              <div className="font-semibold">
                {T("cmiAccept", "J'accepte les conditions de paiement CMI")} *
              </div>
              <div className="text-xs text-gray-500">
                {T("cmiAcceptHint", "Nécessaire pour traiter votre demande.")}
              </div>
            </div>
          </div>

          {/* message */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {T("message", "Message (optionnel)")}
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-orange-600" />
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => setField("message", e.target.value)}
                className="w-full pl-10 pr-3 py-2 rounded-xl border border-orange-200 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:outline-none"
                placeholder={T("messageHint", "Ex: Je veux un guide, départ tôt, etc.")}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full md:w-auto px-5 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 font-semibold text-gray-700"
          >
            {T("cancel", "Annuler")}
          </button>

          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onSubmit?.(form)}
            className={`w-full md:flex-1 px-5 py-2 rounded-xl font-semibold shadow-md transition-all ${
              canSubmit
                ? "bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            {sending
              ? T("sending", "Envoi en cours...")
              : paymentMode === "email"
              ? T("sendRequest", "Envoyer la demande")
              : T("reserveAndPay", "Réserver et payer")}
          </button>
        </div>

        <p className="text-xs text-gray-500">
          {T("reserveHint", "Après confirmation, nous traiterons votre demande et nous vous contacterons.")}
        </p>
      </div>
    </ModalShell>
  );
};

// ---------- Page ----------
const CircuitPage = ({ language }) => {
  const T = (key, fallback) => {
    const v = tr(key, language);
    return v === key ? fallback : v;
  };

  const [loading, setLoading] = useState(false);
  const [circuits, setCircuits] = useState([]);
  const [error, setError] = useState("");

  // Date choisie dans le hero (?date) → passée à la modale de réservation.
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get("date") || "";

  const [selectedCircuit, setSelectedCircuit] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [reserveOpen, setReserveOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [paymentMode, setPaymentMode] = useState("direct"); // "direct" (CMI) ou "email" (demande + lien admin)
  const [activeCity, setActiveCity] = useState("all");
  const [, setActiveApiBase] = useState(API_BASE_CANDIDATES[0] || ENV_API_BASE);

  const openDetails = (c) => {
    setSelectedCircuit(c);
    setDetailsOpen(true);
  };

  const openReserve = (c) => {
    setSelectedCircuit(c);
    setReserveOpen(true);
  };

  const fetchCircuits = async () => {
    try {
      setLoading(true);
      setError("");

      let lastError = null;

      for (const base of API_BASE_CANDIDATES) {
        try {
          const res = await fetch(`${base}${CIRCUITS_PATH}?page=1&limit=50`, {
            method: "GET",
          });

          if (!res.ok) {
            const txt = await res.text().catch(() => "");
            throw new Error(`HTTP ${res.status} - ${txt || "Erreur API"}`);
          }

          const data = await res.json();

          const list =
            Array.isArray(data) ? data
            : Array.isArray(data?.circuits) ? data.circuits
            : Array.isArray(data?.data) ? data.data
            : [];

          setActiveApiBase(base);
          setCircuits(list);
          lastError = null;
          break;
        } catch (e) {
          lastError = e;
        }
      }

      if (lastError) {
        throw new Error(`Impossible de contacter l'API (Locale ou Heroku). Démarrez le backend local sur le bon port (npm run dev).`);
      }
    } catch (e) {
      console.error(e);
      setError(T("circuitLoadError", "Erreur lors du chargement des circuits."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCircuits();
    getPaymentMode("circuit").then(setPaymentMode).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cityFilters = useMemo(() => {
    const set = new Set();
    circuits.forEach((c) => {
      stopsFromCircuit(c).forEach((city) => set.add(city));
    });
    return ["all", ...Array.from(set).slice(0, 8)];
  }, [circuits]);

  const displayedCircuits = useMemo(() => {
    if (activeCity === "all") return circuits;
    return circuits.filter((c) => stopsFromCircuit(c).includes(activeCity));
  }, [circuits, activeCity]);

  const featuredCircuit = displayedCircuits[0] || null;
  const regularCircuits = displayedCircuits.slice(featuredCircuit ? 1 : 0);

  /**
   * ✅ Ici on branche VRAIMENT l'API existante:
   * POST /api/reservations/circuits/request
   *
   * ⚠️ Ton endpoint /request est une "demande" et ton schema exige:
   * destination, dateDebut, duree, travelType, adresse, telephone, acceptedCmi, etc.
   * On va donc mapper le circuit vers ces champs.
   */
  const handleSubmit = async (reservationForm) => {
  if (!selectedCircuit?._id) return;

  if (!reservationForm.acceptedCmi) {
    toast.error(T("cmiRequired", "Veuillez accepter les conditions de paiement CMI."));
    return;
  }

  try {
    setSending(true);

    const raw = localStorage.getItem("currentUser");
    let currentUser = null;
    try {
      currentUser = raw ? JSON.parse(raw) : null;
    } catch {}

    const participants = Number(reservationForm.participants || 1);

    // ====== MODE "EMAIL" : on enregistre une demande, l'admin enverra le lien de paiement ======
    if (paymentMode === "email") {
      const base = API_BASE_CANDIDATES[0] || ENV_API_BASE;
      const demandePayload = {
        circuitId: selectedCircuit._id,
        travelDate: reservationForm.date,
        participants,
        client: {
          fullName: reservationForm.fullName,
          email: reservationForm.email,
          phone: reservationForm.phone,
          address: reservationForm.address,
        },
        acceptedCmi: !!reservationForm.acceptedCmi,
        travelType: reservationForm.travelType,
        message: reservationForm.message || "",
        compteId: currentUser?._id || null,
        lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
      };

      const res = await fetch(`${base}/api/reservations/circuits/circuit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(demandePayload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          (Array.isArray(data?.errors) && data.errors.length
            ? data.errors.map((x) => x?.msg || x?.message || x).join(" • ")
            : "") ||
          data?.message ||
          "Erreur lors de l'envoi de la demande.";
        throw new Error(msg);
      }

      toast.success(
        T("demandeSent", "Demande envoyée ✅ Vous recevrez un lien de paiement par email après validation."),
        { duration: 6000 }
      );
      setReserveOpen(false);
      setSending(false);
      return;
    }

    // ====== MODE "DIRECT" : paiement CMI immédiat ======
    const prixTotal = Number(selectedCircuit.prix || 0) * participants;

    if (!Number.isFinite(prixTotal) || prixTotal <= 0) {
      toast.error(T("invalidAmount", "Montant invalide pour ce circuit."));
      setSending(false);
      return;
    }

    // ✅ Payload attendu par POST /api/cmi/circuit/create
    const payload = {
      circuitId: selectedCircuit._id,
      dateDepart: reservationForm.date,
      nbAdultes: participants,
      nbEnfants: 0,
      utilisateur: reservationForm.fullName || currentUser?.username || "",
      email: reservationForm.email || currentUser?.email || "",
      fullName: reservationForm.fullName || "",
      phone: reservationForm.phone || "",
      address: reservationForm.address || "",
      commentaires: reservationForm.message || "",
      travelType: reservationForm.travelType || "",
      compteId: currentUser?._id || null,
      lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
      prixTotal, // ✅ montant envoyé à CMI
    };

    toast.loading(T("processingReservation", "Préparation du paiement..."), { id: "cmi-circuit" });

    // ✅ Appel CMI → renvoie du HTML (form + auto-submit vers la passerelle)
    const html = await cmiService.createCircuitPayment(payload);

    toast.success(T("redirectingToPayment", "Redirection vers le paiement sécurisé..."), {
      id: "cmi-circuit",
      duration: 1500,
    });

    setReserveOpen(false);

    // ✅ Injecter l'HTML CMI (même logique que les hôtels / CircuitDetail)
    setTimeout(() => {
      document.open();
      document.write(html);
      document.close();
    }, 600);
  } catch (e) {
    console.error("Erreur CMI circuit:", e);
    toast.error(
      e?.response?.data?.message || e?.message || T("reserveError", "Erreur lors du paiement du circuit."),
      { id: "cmi-circuit" }
    );
    setSending(false);
  }
};


  return (
    <div
      className="py-24 min-h-screen relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #fff8ef 0%, #f4e4cf 45%, #f8f5ef 100%)" }}
    >
      {/* Pattern zellij */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${zelijPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "400px",
          backgroundAttachment: "fixed",
          opacity: 0.4,
        }}
      />
      <div className="absolute inset-0 bg-white/80 z-0 pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-12">
        <section className="mb-10 rounded-3xl overflow-hidden shadow-2xl border border-orange-200/70">
          <div className="grid grid-cols-1 lg:grid-cols-5">
            <div className="lg:col-span-3 relative min-h-[280px] lg:min-h-[340px]">
              <img
                src={circuitMoodImages[0]}
                alt="circuits maroc"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/40 to-transparent" />
              <div className="relative z-10 p-6 md:p-10 text-white max-w-2xl">
                <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-orange-200 mb-4">
                  <Sparkles className="w-4 h-4" />
                  Collection circuits
                </p>
                <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">
                  {T("circuitsListTitle", "Nos Circuits")}
                </h1>
                <p className="mt-4 text-orange-50/90 text-sm md:text-lg">
                  Une vitrine plus premium, orientée image et conversion, pour une présentation digne d'une agence de voyage pro.
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 grid grid-cols-2 lg:grid-cols-1 bg-stone-900">
              {[circuitMoodImages[1], circuitMoodImages[2]].map((img, idx) => (
                <div key={idx} className="relative min-h-[140px] lg:min-h-[170px]">
                  <img src={img} alt={`mood-${idx}`} className="absolute inset-0 w-full h-full object-cover opacity-80" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-black/10" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Title */}
        <div className="text-center mb-10">
          <p className="text-lg text-gray-700 max-w-2xl mx-auto opacity-90">
            {T("circuitsListSubtitle", "Choisissez un circuit et réservez en quelques clics.")}
          </p>
        </div>

        {!loading && circuits.length > 0 && (
          <div className="mb-8 flex flex-wrap items-center justify-center gap-2">
            {cityFilters.map((city) => (
              <button
                key={city}
                type="button"
                onClick={() => setActiveCity(city)}
                className={`px-4 py-2 rounded-full text-sm border transition-all ${
                  activeCity === city
                    ? "bg-orange-600 text-white border-orange-600 shadow-md"
                    : "bg-white/90 text-gray-700 border-orange-200 hover:bg-orange-50"
                }`}
              >
                {city === "all" ? "Tous les circuits" : city}
              </button>
            ))}
          </div>
        )}

        {/* states */}
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md">
            {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-white/90 rounded-2xl shadow-xl p-4 border border-orange-100 animate-pulse"
              >
                <div className="h-40 rounded-xl bg-gray-200" />
                <div className="mt-4 h-4 bg-gray-200 rounded w-3/4" />
                <div className="mt-2 h-3 bg-gray-200 rounded w-2/3" />
                <div className="mt-4 h-10 bg-gray-200 rounded-xl" />
              </div>
            ))}
          </div>
        ) : displayedCircuits.length === 0 ? (
          <div className="bg-white/90 rounded-2xl shadow-xl p-6 border border-orange-100 text-center text-gray-700">
            {T("noCircuits", "Aucun circuit disponible pour le moment.")}
          </div>
        ) : (
          <div className="space-y-8">
            {featuredCircuit && (
              <article className="grid grid-cols-1 lg:grid-cols-2 rounded-3xl overflow-hidden border border-orange-100 bg-white/95 shadow-2xl">
                <div className="min-h-[320px]">
                  <img
                    src={getCircuitImage(featuredCircuit)}
                    alt={featuredCircuit?.titre || "Circuit"}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="p-6 md:p-8 flex flex-col justify-between gap-5">
                  <div>
                    <div className="inline-flex rounded-full px-3 py-1 text-xs font-semibold bg-orange-100 text-orange-700 mb-3">
                      Circuit à la une
                    </div>
                    <h2 className="text-2xl md:text-4xl font-black text-gray-900 leading-tight">
                      {featuredCircuit?.titre}
                    </h2>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {featuredCircuit?.duree && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                          {featuredCircuit.duree}
                        </span>
                      )}
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
                        {formatPrice(featuredCircuit?.prix)}
                      </span>
                    </div>

                    <p className="mt-4 text-gray-700 leading-relaxed line-clamp-4">
                      {featuredCircuit?.description || "Circuit premium avec itinéraire personnalisé et expérience immersive."}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => openDetails(featuredCircuit)}
                      className="px-5 py-3 rounded-xl border border-orange-200 bg-white hover:bg-orange-50 font-semibold text-orange-700 flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      {T("viewDetails", "Voir détail")}
                    </button>

                    <button
                      type="button"
                      onClick={() => openReserve(featuredCircuit)}
                      className="px-5 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold hover:from-orange-600 hover:to-red-700 shadow-md"
                    >
                      {T("reserve", "Réserver")}
                    </button>
                  </div>
                </div>
              </article>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularCircuits.map((c) => {
              const stops = stopsFromCircuit(c);
              return (
                <div
                  key={c._id}
                  className="bg-white/90 rounded-2xl shadow-xl border border-orange-100 overflow-hidden hover:shadow-2xl transition-shadow"
                >
                  {/* image */}
                  <div className="h-44 bg-gray-100">
                    <img
                      src={getCircuitImage(c)}
                      alt={c?.titre || "Circuit"}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src =
                          "https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=1200&h=800&fit=crop";
                      }}
                    />
                  </div>

                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-lg font-extrabold text-gray-900 leading-snug truncate">
                          {c?.titre}
                        </div>
                        {c?.duree && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                            <Route className="w-4 h-4 text-orange-600" />
                            <span>{c.duree}</span>
                          </div>
                        )}
                      </div>

                      <div className="shrink-0 px-3 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-100">
                        {formatPrice(c?.prix)}
                      </div>
                    </div>

                    {/* stops */}
                    <div className="text-sm text-gray-700 flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-orange-600 mt-0.5" />
                      <div className="flex-1">
                        {stops.length ? (
                          <>
                            <span className="font-semibold">{T("stops", "Villes")}:</span>{" "}
                            <span className="opacity-90">{stops.join(" → ")}</span>
                          </>
                        ) : (
                          <span className="text-gray-500">
                            {T("noStops", "Villes non spécifiées")}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* buttons */}
                    <div className="pt-2 grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => openDetails(c)}
                        className="px-4 py-2 rounded-xl border border-orange-200 bg-white hover:bg-orange-50 font-semibold text-orange-700 flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        {T("viewDetails", "Voir détail")}
                      </button>

                      <button
                        type="button"
                        onClick={() => openReserve(c)}
                        className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold hover:from-orange-600 hover:to-red-700 shadow-md"
                      >
                        {T("reserve", "Réserver")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>

      {/* Details modal */}
      <CircuitDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        circuit={selectedCircuit}
        T={T}
      />

      {/* Reservation modal */}
      <ReservationModal
        open={reserveOpen}
        onClose={() => setReserveOpen(false)}
        circuit={selectedCircuit}
        language={language}
        T={T}
        onSubmit={handleSubmit}
        sending={sending}
        paymentMode={paymentMode}
        initialDate={initialDate}
      />
    </div>
  );
};

export default CircuitPage;
